/** The Joe Pass search space contains only transpositions of source grips.
 * Harmony matching is deliberately explicit: a missing root/fifth is normal
 * guitar voicing, but a missing third/seventh or a different named alteration
 * is not. No section label or nearest-chord guess decides compatibility. */
import { chordSymbol, getChordType, type ChordTypeId } from './chords';
import { spellVoicingMidis } from './chordSpelling';
import { JOE_PASS_SHAPES, type JoePassShape } from './joePassShapes';
import { pitchClassOf, sharpName } from './notes';
import { frettedMidi, noteAtFret, type StringSet } from './tuning';
import type { ChordVoicing, Fingering, Inversion } from './voicing';

interface HarmonyRule {
  required: readonly number[];
  allowed: readonly number[];
}

const HARMONY_RULES: Record<ChordTypeId, HarmonyRule> = {
  maj7: { required: [4, 11], allowed: [0, 2, 4, 7, 9, 11] },
  maj6: { required: [4, 9], allowed: [0, 2, 4, 7, 9] },
  maj7s5: { required: [4, 8, 11], allowed: [0, 2, 4, 8, 11] },
  maj7b5: { required: [4, 6, 11], allowed: [0, 2, 4, 6, 9, 11] },
  maj9: { required: [2, 4, 11], allowed: [0, 2, 4, 7, 9, 11] },
  maj69: { required: [2, 4, 9], allowed: [0, 2, 4, 7, 9] },
  dom7: { required: [4, 10], allowed: [0, 2, 4, 7, 9, 10] },
  dom7sus4: { required: [5, 10], allowed: [0, 2, 5, 7, 9, 10] },
  dom7s5: { required: [4, 8, 10], allowed: [0, 2, 4, 8, 10] },
  dom7b5: { required: [4, 6, 10], allowed: [0, 2, 4, 6, 9, 10] },
  dom9: { required: [2, 4, 10], allowed: [0, 2, 4, 7, 9, 10] },
  dom13: { required: [4, 9, 10], allowed: [0, 2, 4, 7, 9, 10] },
  dom7b9: { required: [1, 4, 10], allowed: [0, 1, 4, 7, 9, 10] },
  dom7s9: { required: [3, 4, 10], allowed: [0, 3, 4, 7, 9, 10] },
  min6: { required: [3, 9], allowed: [0, 2, 3, 7, 9] },
  min7: { required: [3, 10], allowed: [0, 2, 3, 5, 7, 10] },
  min7b5: { required: [3, 6, 10], allowed: [0, 2, 3, 6, 10] },
  min7s5: { required: [3, 8, 10], allowed: [0, 2, 3, 8, 10] },
  minmaj7: { required: [3, 11], allowed: [0, 2, 3, 7, 11] },
  min9: { required: [2, 3, 10], allowed: [0, 2, 3, 5, 7, 10] },
  min7b5b9: { required: [1, 3, 6, 10], allowed: [0, 1, 3, 6, 10] },
  min7b59: { required: [2, 3, 6, 10], allowed: [0, 2, 3, 6, 10] },
  min69: { required: [2, 3, 9], allowed: [0, 2, 3, 7, 9] },
  dim7: { required: [3, 6, 9], allowed: [0, 3, 6, 9] },
  dimmaj7: { required: [3, 6, 11], allowed: [0, 3, 6, 11] },
  maj: { required: [0, 4, 7], allowed: [0, 4, 7] },
  min: { required: [0, 3, 7], allowed: [0, 3, 7] },
  aug: { required: [0, 4, 8], allowed: [0, 4, 8] },
  dim: { required: [0, 3, 6], allowed: [0, 3, 6] },
};

const mod12 = (n: number) => ((n % 12) + 12) % 12;
const catalogueCache = new Map<string, ChordVoicing[]>();
const copyVoicings = (voicings: ChordVoicing[]) =>
  voicings.map((v) => ({
    ...v,
    frets: [...v.frets],
    voicing: [...v.voicing],
  }));

export function joePassMatchesHarmony(
  midis: readonly number[],
  root: string,
  type: ChordTypeId,
): boolean {
  const pcs = new Set(midis.map((midi) => mod12(midi - pitchClassOf(root))));
  const rule = HARMONY_RULES[type];
  return (
    pcs.size >= 3 &&
    rule.required.every((pc) => pcs.has(pc)) &&
    [...pcs].every((pc) => rule.allowed.includes(pc))
  );
}

/** Notes from physical tuning, independent of an editorial chord name. */
export function joePassShapeNotes(shape: Pick<JoePassShape, 'frets'>): {
  string: number;
  fret: number;
  midi: number;
  note: string;
}[] {
  return shape.frets.flatMap((fret, string) =>
    fret == null
      ? []
      : [
          {
            string,
            fret,
            midi: frettedMidi(string, fret),
            note: noteAtFret(string, fret),
          },
        ],
  );
}

/** Original library grip. No guessed four-note formula is needed to draw,
 * inspect the sounding pitches, or audition it. */
export function joePassShapeFingering(shape: JoePassShape): Fingering {
  const notes = joePassShapeNotes(shape);
  return {
    frets: [...shape.frets],
    voicing: notes.map((note) => note.note),
    voicingFamily: 'joe-pass',
    stringLayout: 'joe-pass',
    stringSet: notes.some((note) => note.string === 5) ? 'upper' : 'middle',
    sourceShapeId: shape.id,
  };
}

export interface JoePassEnumerateOptions {
  /** Retained for caller compatibility; this family always searches every
   * physical string mask. Individual grips report their actual register. */
  stringSet?: StringSet;
  maxFret?: number;
  displayRoot?: string;
  /** Useful for independent source-fixture checks. Production uses the full
   * transcribed catalogue. */
  shapes?: readonly JoePassShape[];
}

/** Move the complete source grip without making an open-string shape into
 * a new, larger fretted stretch. Exact wide source grips remain available. */
export function joePassTransposedFrets(
  shape: Pick<JoePassShape, 'frets'>,
  semitones: number,
  maxFret = 20,
): (number | null)[] | null {
  if (!Number.isInteger(semitones)) return null;
  const frets = shape.frets.map((fret) => (fret == null ? null : fret + semitones));
  if (frets.some((fret) => fret != null && (fret < 0 || fret > maxFret))) return null;
  const span = (values: readonly (number | null)[]) => {
    const fretted = values.filter((fret): fret is number => fret != null && fret > 0);
    return fretted.length ? Math.max(...fretted) - Math.min(...fretted) : 0;
  };
  return span(frets) <= Math.max(4, span(shape.frets)) ? frets : null;
}

/** Every compatible whole-grip transposition on the playable neck. Different
 * source diagrams can describe the same resulting frets; admit those once. */
export function enumerateJoePassVoicings(
  root: string,
  chordType: ChordTypeId,
  options: JoePassEnumerateOptions = {},
): ChordVoicing[] {
  const maxFret = options.maxFret ?? 20;
  const displayRoot = options.displayRoot ?? root;
  const cacheKey = `${pitchClassOf(root)}:${displayRoot}:${chordType}:${maxFret}`;
  if (!options.shapes && catalogueCache.has(cacheKey)) {
    return copyVoicings(catalogueCache.get(cacheKey)!);
  }
  const result: ChordVoicing[] = [];
  const seen = new Set<string>();
  for (const shape of options.shapes ?? JOE_PASS_SHAPES) {
    const notes = joePassShapeNotes(shape);
    if (notes.length < 3) continue;
    const min = Math.min(...notes.map((note) => note.fret));
    const max = Math.max(...notes.map((note) => note.fret));
    for (let shift = -min; shift <= maxFret - max; shift += 1) {
      const midis = notes.map((note) => note.midi + shift);
      if (!joePassMatchesHarmony(midis, root, chordType)) continue;
      const frets = joePassTransposedFrets(shape, shift, maxFret);
      if (!frets) continue;
      const key = frets.map((fret) => fret ?? 'x').join(',');
      if (seen.has(key)) continue;
      seen.add(key);
      const bass = mod12(Math.min(...midis) - pitchClassOf(root));
      const rotation = getChordType(chordType).intervals.findIndex((pc) => mod12(pc) === bass);
      result.push({
        ...joePassShapeFingering(shape),
        frets,
        voicing: spellVoicingMidis(midis, displayRoot, chordType, sharpName),
        root: sharpName(pitchClassOf(root)),
        displayRoot,
        chordType,
        inversion: (rotation < 0 ? 0 : rotation) as Inversion,
        symbol: chordSymbol(displayRoot, chordType),
      });
    }
  }
  // Keep original source order as the last tie break, with a comfortable
  // lower position first. Sorting never removes a candidate from planning.
  result.sort((a, b) => {
    const position = (f: Fingering) => Math.min(...f.frets.filter((n): n is number => n != null));
    return position(a) - position(b);
  });
  if (!options.shapes) catalogueCache.set(cacheKey, result);
  return copyVoicings(result);
}
